import React, {PureComponent} from 'react';
import {Mention, MentionsInput} from 'react-mentions';
import {evite} from 'evite';
import * as mentionUtils from './mention-utils';

const COMPLETE_LIMIT = 7;
let instanceCount = 0;

export class MentionEditor extends PureComponent {
  constructor(props) {
    super(props);
    const initialValue = mentionUtils.fromOurMentions(props.initialContent, props.initialMentions);

    this.state = {
      value: initialValue,
      hasChanged: false,
    };

    this.className = `editor__${instanceCount++}`;

    if (this.props.publicRef) {
      this.props.publicRef(this);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps, nextState) {
    if (this.props.initialContent !== nextProps.initialContent)
      this.setState({value: nextProps.initialContent});

    if (this.props.clearFromParent !== nextProps.clearFromParent) {
      this.clearComment();
    }
  }

  componentWillUnmount() {
    if (this.props.publicRef) {
      this.props.publicRef(null);
    }
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      this.handleFocus();
    }
  }

  clearComment() {
    this.setComment('');
  }

  get comment() {
    return (this.state.plainValue ? this.state.plainValue : this.props.initialContent) || '';
  }

  get mentions() {
    return mentionUtils.sortMentions('asc', this.state.mentions || this.props.initialMentions);
  }

  get hasFocus() {
    return this.state.hasFocus;
  }

  handleBlur = () => {
    this.setState({hasFocus: false});

    if (this.props.onBlur) {
      return this.props.onBlur();
    }
  };

  handleFocus = () => {
    this.setState({hasFocus: true});

    if (this.props.onFocus) {
      return this.props.onFocus();
    }
  };

  /**
   * focus on the mention editor
   */
  focus = () => {
    document.querySelector(`.${this.className} textarea`).focus();
  };

  handlePost = () => {
    this.props.handleCommentSubmit();
    this.clearComment();
  };

  render() {
    const {state, className, props} = this;
    const {placeholder, focusPlaceholder, singleLineInput, isOldStyle, dataQaId, id} = props;
    const {hasFocus} = state;

    const classList = ['mention-editor'];
    if (className) classList.push(className);
    if (hasFocus) classList.push('has-focus');
    if (this.state.value) classList.push('has-content');
    if (singleLineInput) classList.push('single-line-input');

    const defaultPlaceholder = 'Write a comment...';
    const defaultFocusPlaceholder = evite.features.isEnabled('mentions')
      ? 'Use @ to mention a guest.'
      : placeholder || defaultPlaceholder;

    const placeholderText = hasFocus
      ? focusPlaceholder || defaultFocusPlaceholder
      : placeholder || defaultPlaceholder;

    const disablePostButton = !this.state.value || !this.state.hasChanged;

    const style = {
      suggestions: {
        zIndex: 11,
        list: {
          backgroundColor: 'white',
          border: 'none',
          minWidth: '100px',
        },
      },
    };

    if (this.props.suggestionsOnTop) {
      classList.push('direction-up');
      Object.assign(style.suggestions.list, {
        overflow: 'auto',
        position: 'absolute',
        bottom: '17px',
      });
    }

    return (
      <div className={classList.join(' ')} data-qa-id={dataQaId || 'mention-editor'}>
        <MentionsInput
          value={this.state.value || ''}
          className="mention-input"
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          placeholder={placeholderText}
          singleLine={singleLineInput}
          style={style}
          allowSpaceInQuery
          id={id}
          data-qa-id={dataQaId || 'mention-input__textarea'}
        >
          <Mention
            trigger="@"
            className="mention-complete"
            appendSpaceOnAdd
            data={this.fetchUsers}
          />
        </MentionsInput>
        {!isOldStyle && (
          <button
            type="submit"
            onClick={this.handlePost}
            className={`comment-editor__submit unstyled-button link ${
              disablePostButton ? 'disabled' : ''
            }`}
            disabled={this.state.value.trim().length === 0}
          >
            Post
          </button>
        )}
      </div>
    );
  }

  handleChange = (event, newValue, newPlainTextValue, mentions) => {
    this.setState({
      value: newValue,
      plainValue: newPlainTextValue,
      mentions: mentionUtils.toOurMentionFormat(mentions),
      hasChanged: true,
    });

    // fire call to parent component so it can be aware of comment changes
    this.props.onChange(newValue);
  };

  setMention(id, first, last) {
    const name = `${first} ${last}`.trim();
    const newComment = `@[${name}](${id}) `;
    this.setState({
      value: newComment,
    });
  }

  /**
   * fetch user suggestions for the mention autocomplete
   *
   * @param search
   * @param callback func({id<string>, display<string>})
   */
  fetchUsers = async (search, callback) => {
    const url = evite.urls.get('ajax_event_guestlist_search', {
      search_term: search || '',
      event_id: window.evite_event.id,
      gid: window.guest_id,
      limit: COMPLETE_LIMIT,
    });

    try {
      const response = await evite.fetch(url, {
        method: 'get',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        const guests = await response.json();

        const suggestions = [];
        for (const guest of guests) {
          suggestions.push({id: guest.user_id, display: guest.primary_name});
        }

        callback(suggestions);
      }
    } catch (e) {
      evite.error('Error in mentions');
      evite.error(e);
      Raven.captureException(e);
      callback([]);
    }
  };

  setComment(value) {
    this.setState({
      value,
      plainValue: '',
      mentions: [],
    });
  }
}
